import { ApolloClient } from '@apollo/client';

import type { StimulusResourceFragment } from '../shared/stimulus/stimulus.graphql.generated';
import type { InteractiveVideoFragment } from './lesson-builder.graphql.generated';
import type { InteractiveAudioFragment } from './lesson-builder.graphql.generated';
import type { InteractiveLinkFragment } from './lesson-builder.graphql.generated';
import type { InteractiveWebToolFragment } from './lesson-builder.graphql.generated';
import type { InteractiveGuidedResearchFragment } from './lesson-builder.graphql.generated';
import type { InteractiveEbookInteractiveFragment } from './lesson-builder.graphql.generated';
import type { InteractiveInteractiveFragment } from './lesson-builder.graphql.generated';
import type { InteractiveDocumentFragment } from './lesson-builder.graphql.generated';
import type { InteractiveEbookFragment } from './lesson-builder.graphql.generated';
import {
  ACTIVITIES_CONTENT_STATUS_DRAFT,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import type { Pluck } from 'lib/gql/pluck';

type StimulusResourceProperties =
  | Pluck<InteractiveVideoFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveAudioFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveLinkFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveWebToolFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveGuidedResearchFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveInteractiveFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveDocumentFragment, 'stimulusResourceProperties'>
  | Pluck<InteractiveEbookFragment, 'stimulusResourceProperties'>;

function updateComponentStimulusResourcePropertiesInCache({
  client,
  componentId,
  componentType,
  data,
}: {
  client: ApolloClient<any>;
  componentId: string;
  componentType: string;
  data: StimulusResourceProperties;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: componentId,
      __typename: componentType,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      stimulusResourceProperties: existing => {
        return {
          ...existing,
          ...data,
        };
      },
    },
  });
}

function updateStimulusImageInCache({
  client,
  componentId,
  data,
}: {
  client: ApolloClient<any>;
  componentId: string;
  data: Extract<
    StimulusResourceFragment,
    { __typename: 'StimulusResourceImage' }
  >;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: componentId,
      __typename: 'ActivitiesImageComponent',
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      properties: existing => {
        return {
          ...existing,
          stimulusId: data.id,
          imageUrls: data.image?.responsive,
        };
      },
      stimulusResourceProperties: () => {
        return {
          type: 'image',
          altText: data.altText,
          link: data.link,
          description: data.description,
          resourceName: data.name,
          coverImage: {
            key: data.coverImage?.key,
            fileName: data.coverImage?.fileName,
            original: data.coverImage?.original,
            responsive: data.coverImage?.responsive,
          },
          image: {
            key: data.image?.key,
            fileName: data.image?.fileName,
            original: data.image?.original,
            responsive: data.image?.responsive,
          },
        };
      },
    },
  });
}

export function updateStimulusActivityInCache({
  client,
  componentId,
  data,
}: {
  client: ApolloClient<any>;
  componentId: string;
  data: StimulusResourceFragment;
}) {
  const baseData: {
    resourceName: string | null;
    coverImage: Pluck<
      InteractiveVideoFragment,
      'stimulusResourceProperties.coverImage'
    > | null;
    description: string | null;
    xlCoverImage: Pluck<
      InteractiveVideoFragment,
      'stimulusResourceProperties.xlCoverImage'
    > | null;
    xlCoverImageUrls: Pluck<
      InteractiveVideoFragment,
      'stimulusResourceProperties.xlCoverImageUrls'
    > | null;
  } = {
    resourceName: data.name,
    coverImage: data.coverImage,
    description: data.description,
    xlCoverImage: data.xlCoverImage,
    xlCoverImageUrls: null,
  };

  switch (data.__typename) {
    case 'StimulusResourceVideo':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesVideoComponent',
        data: {
          ...baseData,
          __typename: 'StimulusVideoProperties',
          videoData: data.videos,
          link: data.slug,
          xlCoverImageUrls: data.xlCoverImageUrls,
          attributions: data.attributions,
        } as Pluck<InteractiveVideoFragment, 'stimulusResourceProperties'>,
      });
      break;
    case 'StimulusResourceAudio':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesAudioComponent',
        data: {
          ...baseData,
          __typename: 'StimulusAudioProperties',
          videoData: data.videos,
          link: data.slug,
          xlCoverImageUrls: data.xlCoverImageUrls,
          attributions: data.attributions,
        } as Pluck<InteractiveAudioFragment, 'stimulusResourceProperties'>,
      });
      break;
    case 'StimulusResourceImage':
      updateStimulusImageInCache({
        client,
        componentId,
        data,
      });
      break;
    case 'StimulusResourceLink':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesLinkComponent',
        data: {
          ...baseData,
          __typename: 'StimulusLinkProperties',
          link: data.link,
        } as Pluck<InteractiveLinkFragment, 'stimulusResourceProperties'>,
      });
      break;
    case 'StimulusResourceWebTool':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesWebToolComponent',
        data: {
          ...baseData,
          __typename: 'StimulusWebToolProperties',
          link: data.link,
        } as Pluck<InteractiveWebToolFragment, 'stimulusResourceProperties'>,
      });
      break;
    case 'StimulusResourceGuidedResearch':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesGuidedResearchComponent',
        data: {
          ...baseData,
          __typename: 'StimulusGuidedResearchProperties',
          links: data.links,
          link: null,
          slug: data.slug,
        } as Pluck<
          InteractiveGuidedResearchFragment,
          'stimulusResourceProperties'
        >,
      });
      break;
    case 'StimulusResourceEBookInteractive':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesInteractiveEbookComponent',
        data: {
          ...baseData,
          __typename: 'InteractiveStimulusProperties',
          link: data.link,
          slug: data.slug,
          xlCoverImageUrls: data.xlCoverImageUrls,
        } as Pluck<
          InteractiveEbookInteractiveFragment,
          'stimulusResourceProperties'
        >,
      });
      break;
    case 'StimulusResourceInteractive':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesInteractiveComponent',
        data: {
          ...baseData,
          __typename: 'InteractiveStimulusProperties',
          link: data.link,
          slug: data.slug,
          xlCoverImageUrls: data.xlCoverImageUrls,
        } as Pluck<
          InteractiveInteractiveFragment,
          'stimulusResourceProperties'
        >,
      });
      break;
    case 'StimulusResourceDocument':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesDocumentComponent',
        data: {
          ...baseData,
          __typename: 'StimulusDocumentProperties',
          documentKey: data.documentKey,
          link: data.link,
          fileName: data.fileName,
          xlCoverImageUrls: data.xlCoverImageUrls,
        } as Pluck<InteractiveDocumentFragment, 'stimulusResourceProperties'>,
      });
      break;
    case 'StimulusResourceEBook':
      updateComponentStimulusResourcePropertiesInCache({
        client,
        componentId,
        componentType: 'ActivitiesEbookComponent',
        data: {
          ...baseData,
          __typename: 'StimulusEbookProperties',
          documentKey: data.documentKey,
          link: data.link,
          fileName: data.fileName,
          xlCoverImageUrls: data.xlCoverImageUrls,
        } as Pluck<InteractiveEbookFragment, 'stimulusResourceProperties'>,
      });
      break;
  }
}
